/**
 * Interdiction (plan R17, §2.9 flow 5; spec §4.2; D-TBD 17). While cruising (never on a lane), every `rollEveryS` the
 * encounter port's rate for the system is rolled (`rate × rollEveryS / 60`), except within `stationExclusionM` of a
 * station, within `graceAfterArrivalS` of arriving and within `cooldownS` of the last interdiction. A hit starts R6's
 * tug-of-war against the rolled group's best pilot: the ship keeps flying along its nose while its speed decays toward
 * `speedFloor × allowed`. Escaping resumes the cruise; failing drops the ship out (`dropSpeed`, `failCooldownS`) and
 * spawns the group ahead of the nose, facing the player.
 *
 * `rollInterdiction` and `stepInterdicted` run per tick and allocate nothing (D-014); starting a tug and spawning
 * the group are rare events.
 *
 * M10 P4b6b (P4b-15): the flown hull's band scales the encounter port's rate and group (`reach-travel.json
 * interdiction.bandRateMult` and `bandTierStep`, validated here; the bridge applies them, `bridge/m2/encounters.ts`).
 * A moored hull is docked (moored = docked) and never cruises, and its ring lies inside `stationExclusionM`.
 */
import { z } from 'zod'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import { nextFloat } from '../../core/rng'
import { emitReach, REV } from '../../reach/events'
import type { ReachCore, SpawnShipSpec } from '../../reach/runtime/types'
import { RAIDER_TEAM, type Sim } from '../../sim'
import { Pos64 } from '../../world/components'
import travelJson from '../../data/reach-travel.json'
import type { ShipBand } from '../../data/schemas'
import type { PilotProfile, TravelConfig } from '../config'
import { startTug, stepTug, type MassLockInput, type TugResult } from '../math'
import { dropOut } from './transitions'
import { shipFromEcs, shipToEcs, turnDetached } from './flight'
import { stepTugShip, TARGET_NONE } from './kinematics'
import { DROP, INTERDICT_END, type TravelRuntime } from './state'

/** A band's scale: each band named, XL optional (a band left out is neutral). */
function perBand<T extends z.ZodType>(value: T) {
  return z.strictObject({ S: value, M: value, L: value, XL: value.optional() })
}

/** The band keys of `reach-travel.json interdiction` (the travel config schema strips them; they are read here). */
export const interdictionBandSchema = z.object({
  /** The interdiction rate multiplier by the flown hull's band [P: S 1, M 1, L 1.5]. */
  bandRateMult: perBand(z.number().positive()),
  /** Cargo tiers the interdictor group steps up by the flown hull's band [P: an L draws the next tier]. */
  bandTierStep: perBand(z.number().int().nonnegative()),
})

export type InterdictionBandConfig = z.infer<typeof interdictionBandSchema>

/** Parses and validates the band keys of `reach-travel.json interdiction` (throws a ZodError naming the bad field). */
export function loadInterdictionBandConfig(raw: unknown = travelJson.interdiction): InterdictionBandConfig {
  return interdictionBandSchema.parse(raw)
}

/** The rate multiplier for a flown band; 1 with no band (no flown hull) or a band left out. Allocation-free. */
export function bandRateMult(cfg: InterdictionBandConfig, band: ShipBand | null): number {
  return band === null ? 1 : (cfg.bandRateMult[band] ?? 1)
}

/** The cargo tiers the group steps up for a flown band; 0 with no band or a band left out. Allocation-free. */
export function bandTierStep(cfg: InterdictionBandConfig, band: ShipBand | null): number {
  return band === null ? 0 : (cfg.bandTierStep[band] ?? 0)
}

/** Tug `interdictStart`/`interdictEnd` a when no group exists (the group spawns only on a failure). */
export const NO_GROUP = -1

/**
 * The distance (m) from p to the nearest station centre in the mass-lock input; Infinity with none. The interdiction
 * exclusion stays from the centre (scale build SC-5a2a): 20 km still clears a 2 km hull by ~18 km.
 */
export function nearestStationDistance(input: MassLockInput, px: number, py: number, pz: number): number {
  let best = Infinity
  for (let i = 0; i < input.stationCount; i++) {
    const dx = input.stations[i * 3] - px
    const dy = input.stations[i * 3 + 1] - py
    const dz = input.stations[i * 3 + 2] - pz
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 < best) best = d2
  }
  return Math.sqrt(best)
}

/** Whether an interdiction may be rolled now: far enough from stations, past the arrival grace and the cooldown. */
export function interdictionAllowed(now: number, arrivedAt: number, lastAt: number, stationDistance: number, cfg: TravelConfig): boolean {
  const ic = cfg.interdiction
  return stationDistance >= ic.stationExclusionM && now - arrivedAt >= ic.graceAfterArrivalS && now - lastAt >= ic.cooldownS
}

const PROFILE_RANK: Readonly<Record<PilotProfile, number>> = { rookie: 0, veteran: 1, ace: 2 }

/** The best pilot profile in a group ('rookie' for an empty one). */
export function highestProfile(group: readonly SpawnShipSpec[]): PilotProfile {
  let best: PilotProfile = 'rookie'
  for (let i = 0; i < group.length; i++) if (PROFILE_RANK[group[i].profile] > PROFILE_RANK[best]) best = group[i].profile
  return best
}

/** Starts a tug (rare): rolls the system's interdictor group and places the escape vector off the cruise heading. */
export function startInterdiction(rt: TravelRuntime, core: ReachCore, eid: number, systemIndex: number): void {
  const t = core.travel
  const cfg = rt.configs.travel
  const group = core.encounters.interdictorGroup(systemIndex, core.rng)
  rt.interdictors = group
  const s = rt.ship
  const hx = s.speed > 0 ? s.vx : rt.nose.x
  const hy = s.speed > 0 ? s.vy : rt.nose.y
  const hz = s.speed > 0 ? s.vz : rt.nose.z
  startTug(rt.tug, hx, hy, hz, highestProfile(group), core.rng, cfg)
  t.mode = 'interdicted'
  writeTug(rt, core)
  logger.info(`reach: interdicted in cruise (${group.length} ship types, ${rt.tug.profile})`)
  emitReach(core.events, REV.interdictStart, NO_GROUP, 0, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** Every `rollEveryS` of cruise, rolls for an interdiction (after the move). Allocation-free. */
export function rollInterdiction(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const cfg = rt.configs.travel
  const ic = cfg.interdiction
  const input = rt.massLock
  const layout = rt.layout
  if (input === null || layout === null) return
  rt.rollTimer += SIM_DT
  if (rt.rollTimer < ic.rollEveryS - 1e-9) return
  rt.rollTimer -= ic.rollEveryS
  const s = rt.ship
  if (!interdictionAllowed(sim.time, rt.arrivedAt, rt.lastInterdictAt, nearestStationDistance(input, s.x, s.y, s.z), cfg)) return
  const rate = core.encounters.interdictionRatePerMin(layout.systemIndex)
  if (!(rate > 0)) return
  if (nextFloat(core.rng) >= (rate * ic.rollEveryS) / 60) return
  startInterdiction(rt, core, eid, layout.systemIndex)
}

/** Copies the tug's meters and vector into `travel.interdiction` for the HUD, audio and VFX. */
function writeTug(rt: TravelRuntime, core: ReachCore): void {
  const ic = rt.configs.travel.interdiction
  const tug = rt.tug
  const it = core.travel.interdiction
  it.active = true
  it.escape = tug.escape
  it.pull = tug.pull
  it.ex = tug.ex
  it.ey = tug.ey
  it.ez = tug.ez
  const rate = tug.onVector ? 1 : ic.offVectorPullMult
  const left = ((1 - tug.pull) * ic.maxS) / rate
  it.timeLeftS = left > 0 ? left : 0
}

/** Ends the tug in the state (the caller emits `REV.interdictEnd`). */
function endTug(rt: TravelRuntime, core: ReachCore, sim: Sim): void {
  const it = core.travel.interdiction
  it.active = false
  it.escape = 0
  it.pull = 0
  it.timeLeftS = 0
  rt.lastInterdictAt = sim.time
}

/**
 * M8 TR-8b1 (M6 spec §6.4.2 phase a): the pilot jettisoned cargo during the tug, so the raiders let the ship go. The
 * tug ends like an escape: back to cruise, `lastInterdictAt` set (the cooldown applies), the rolled group dropped and
 * never spawned, `REV.interdictEnd` (a `NO_GROUP`, b `INTERDICT_END.jettisoned`). Returns false and changes nothing
 * unless the ship is in a tug. The cargo, the pod and its claim are the caller's (TR-8b2's bridge hook). A rare event.
 */
export function endInterdictionJettisoned(rt: TravelRuntime, core: ReachCore, sim: Sim): boolean {
  const t = core.travel
  if (t.mode !== 'interdicted' || !t.interdiction.active) return false
  endTug(rt, core, sim)
  t.mode = 'cruise'
  rt.interdictors = null
  const s = rt.ship
  logger.info('reach: interdiction ended by a jettison')
  emitReach(core.events, REV.interdictEnd, NO_GROUP, INTERDICT_END.jettisoned, s.x, s.y, s.z, 0)
  return true
}

/** A failed tug (rare): drop out beside the attackers, who spawn ahead of the nose facing the player. */
function failInterdiction(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const ic = rt.configs.travel.interdiction
  endTug(rt, core, sim)
  dropOut(rt, core, sim, eid, DROP.interdicted, ic.dropSpeed, ic.failCooldownS)
  const n = rt.nose
  const ahead = rt.configs.runtime.interdictorAheadM
  const ships = rt.interdictors ?? []
  rt.interdictors = null
  const group =
    ships.length === 0
      ? NO_GROUP
      : core.session.spawnGroup({
          ships,
          center: [Pos64.x[eid] + n.x * ahead, Pos64.y[eid] + n.y * ahead, Pos64.z[eid] + n.z * ahead],
          facing: [-n.x, -n.y, -n.z],
          radiusM: ic.spawnRadiusM,
          team: RAIDER_TEAM,
          kind: 'interdictor',
          despawnM: rt.configs.streaming.transientDespawnM,
        })
  emitReach(core.events, REV.interdictEnd, group, INTERDICT_END.failed, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** One tick of R6's tug with the ship's nose (a leaf: floats stay in fields). */
function tugTick(rt: TravelRuntime, core: ReachCore): TugResult {
  const n = rt.nose
  return stepTug(rt.tug, n.x, n.y, n.z, SIM_DT, core.rng, rt.configs.travel)
}

/**
 * One tick of the tug: turn at the cruise rate, step R6's tug with the nose, keep moving along the nose while the
 * speed decays toward `speedFloor × allowed`. Inside a shell the ship drops (emergency; the tug ends as escaped); an
 * escape resumes the cruise; a failure drops out and spawns the group.
 */
export function stepInterdicted(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  const c = rt.configs.travel.cruise
  const input = rt.massLock
  if (input === null) return
  turnDetached(rt, sim, eid)
  const result = tugTick(rt, core)
  const ship = rt.ship
  shipFromEcs(rt, eid)
  ship.speed = t.speed
  stepTugShip(ship, rt.nose, rt.target, input, rt.configs.travel)
  shipToEcs(rt, eid)
  t.speed = ship.speed
  t.allowed = ship.allowed
  t.targetDistance = rt.target.shape !== TARGET_NONE ? ship.dTarget : 0
  writeTug(rt, core)
  if (ship.dSurface <= 0) {
    endTug(rt, core, sim)
    emitReach(core.events, REV.interdictEnd, NO_GROUP, INTERDICT_END.escaped, ship.x, ship.y, ship.z, 0)
    dropOut(rt, core, sim, eid, DROP.emergency, c.dropSpeed, c.emergencyCooldownS)
    return
  }
  if (result === 'escaped') {
    endTug(rt, core, sim)
    t.mode = 'cruise'
    rt.interdictors = null
    emitReach(core.events, REV.interdictEnd, NO_GROUP, INTERDICT_END.escaped, ship.x, ship.y, ship.z, 0)
  } else if (result === 'failed') failInterdiction(rt, core, sim, eid)
}
